import { derivedSonata, SONATA_IDS } from './sonatas';
import type { PieceData, SectionSpec } from './types';
import { Key, keyName, parseKey, sameKey } from '../music/key';
import type { Measure, NoteEvent } from '../music/piece';
import { mod } from '../music/pitch';
import { buildView, PieceView } from '../ui/model';

/**
 * Exposition and recapitulation, bar by bar. Each bar is summarized by its top and bass notes
 * (onset in the bar, pitch class); two bars are similar when those match under one transposition.
 * The two bar sequences are aligned by dynamic programming, allowing bars to be cut or added, and
 * every column is classified: the same music, the same music moved to another key (usually home),
 * varied, only in the exposition, or only in the recapitulation. Deterministic.
 */

export type ColumnKind = 'same' | 'home' | 'moved' | 'varied' | 'cut' | 'new';

export interface FormColumn {
  /** Bar numbers in the exposition and recapitulation (null where one has no counterpart). */
  e: number | null;
  r: number | null;
  sim: number;
  /** Semitones from the exposition bar up to the recapitulation bar (0–11). */
  shift: number;
  kind: ColumnKind;
  eKey: Key | null;
  rKey: Key | null;
  /** Brought home and turned to the other mode (a major second theme returning in a minor tonic). */
  modeChange?: boolean;
}

export interface FormRun {
  kind: 'varied' | 'cut' | 'new';
  from: number;
  to: number;
}

export interface FormRoom {
  id: string;
  piece: PieceData;
  view: PieceView;
  home: Key;
  expo: { start: number; end: number; sections: SectionSpec[] };
  recap: { start: number; end: number; sections: SectionSpec[] };
  columns: FormColumn[];
  /** Runs of columns where the recapitulation departs from the exposition. */
  runs: FormRun[];
  counts: Record<ColumnKind, number>;
}

/** The share of a bar's top and bass notes two bars have in common, shift applied to the second. */
export interface BarPrint {
  bar: number;
  top: Set<string>;
  bass: Set<string>;
  topPcs: Array<[number, number]>;
  bassPcs: Array<[number, number]>;
  size: number;
}

export function printBar(notes: NoteEvent[], m: Measure): BarPrint {
  const inBar = notes.filter((n) => n.onset >= m.start - 1e-6 && n.onset < m.start + m.length - 1e-6);
  const byOnset = new Map<number, NoteEvent[]>();
  for (const n of inBar) {
    const k = Math.round((n.onset - m.start) * 12);
    byOnset.set(k, [...(byOnset.get(k) ?? []), n]);
  }
  const topPcs: Array<[number, number]> = [];
  const bassPcs: Array<[number, number]> = [];
  for (const [k, list] of byOnset) {
    const sorted = [...list].sort((a, b) => a.midi - b.midi);
    topPcs.push([k, mod(sorted[sorted.length - 1].midi, 12)]);
    if (sorted.length > 1 || sorted[0].line === 0) bassPcs.push([k, mod(sorted[0].midi, 12)]);
  }
  return {
    bar: m.bar,
    top: new Set(topPcs.map(([k, p]) => `${k}:${p}`)),
    bass: new Set(bassPcs.map(([k, p]) => `${k}:${p}`)),
    topPcs,
    bassPcs,
    size: topPcs.length + bassPcs.length,
  };
}

export function similarity(a: BarPrint, b: BarPrint): { sim: number; shift: number } {
  if (!a.size || !b.size) return { sim: a.size === b.size ? 1 : 0, shift: 0 };
  let best = { sim: -1, shift: 0 };
  for (let t = 0; t < 12; t++) {
    let hit = 0;
    for (const [k, p] of a.topPcs) if (b.top.has(`${k}:${mod(p + t, 12)}`)) hit++;
    for (const [k, p] of a.bassPcs) if (b.bass.has(`${k}:${mod(p + t, 12)}`)) hit++;
    const sim = hit / Math.max(a.size, b.size);
    if (sim > best.sim + 1e-9) best = { sim, shift: t };
  }
  return best;
}

const MATCH_FLOOR = 0.35;
const GAP = -0.05;
const SAME = 0.75;
const MODE_FLOOR = 0.45;

function alignBars(expo: BarPrint[], recap: BarPrint[]): Array<{ i: number | null; j: number | null; sim: number; shift: number }> {
  return alignMatrix(expo.map((a) => recap.map((b) => similarity(a, b))));
}

/** Global alignment of two bar sequences from a similarity matrix (rows: first sequence). */
export function alignMatrix(sims: Array<Array<{ sim: number; shift: number }>>): Array<{ i: number | null; j: number | null; sim: number; shift: number }> {
  const n = sims.length;
  const m = n ? sims[0].length : 0;
  const W = m + 1;
  const score = new Float64Array((n + 1) * W);
  const move = new Uint8Array((n + 1) * W);
  for (let i = 1; i <= n; i++) {
    score[i * W] = i * GAP;
    move[i * W] = 2;
  }
  for (let j = 1; j <= m; j++) {
    score[j] = j * GAP;
    move[j] = 3;
  }
  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      // Prefer keeping the same transposition as the previous diagonal step: a small bonus.
      const s = sims[i - 1][j - 1];
      const diag = score[(i - 1) * W + j - 1] + (s.sim - MATCH_FLOOR);
      const up = score[(i - 1) * W + j] + GAP;
      const left = score[i * W + j - 1] + GAP;
      let best = diag;
      let mv = 1;
      if (up > best) {
        best = up;
        mv = 2;
      }
      if (left > best) {
        best = left;
        mv = 3;
      }
      score[i * W + j] = best;
      move[i * W + j] = mv;
    }
  }
  const out: Array<{ i: number | null; j: number | null; sim: number; shift: number }> = [];
  let i = n;
  let j = m;
  while (i > 0 || j > 0) {
    const mv = move[i * W + j];
    if (mv === 1) {
      out.push({ i: i - 1, j: j - 1, ...sims[i - 1][j - 1] });
      i--;
      j--;
    } else if (mv === 2) {
      out.push({ i: i - 1, j: null, sim: 0, shift: 0 });
      i--;
    } else {
      out.push({ i: null, j: j - 1, sim: 0, shift: 0 });
      j--;
    }
  }
  return out.reverse();
}

const isLabel = (s: SectionSpec, re: RegExp) => re.test(s.label.toLowerCase());

/** Below this share of exposition bars matched, a comparison says more about the alignment than the music. */
export const MIN_MATCHED = 0.5;

/** Share of the exposition's bars found again in the recapitulation (in any form). */
export function matchedShare(r: FormRoom): number {
  const expoBars = r.counts.same + r.counts.home + r.counts.moved + r.counts.varied + r.counts.cut;
  return expoBars ? (r.counts.same + r.counts.home + r.counts.moved + r.counts.varied) / expoBars : 0;
}

/**
 * Movements whose analysis names an exposition, a development and a recapitulation, and whose
 * two journeys line up well enough to compare (sonata-rondos that bring their themes back in a
 * new order do not; their pages say so in words instead).
 */
export function formIds(): string[] {
  return SONATA_IDS.filter((id) => {
    const secs = derivedSonata(id)?.piece.sections ?? [];
    const named = secs.some((s) => isLabel(s, /^exposition/)) && secs.some((s) => isLabel(s, /^recapitulation/)) && secs.some((s) => isLabel(s, /^development/));
    if (!named) return false;
    const r = formRoom(id);
    return !!r && matchedShare(r) >= MIN_MATCHED;
  });
}

function keyAtBar(view: PieceView, t: number): Key | null {
  let k: Key | null = null;
  for (const ev of view.events) if (ev.start <= t + 1e-6) k = ev.key;
  return k;
}

const rooms = new Map<string, FormRoom | null>();
export function formRoom(id: string): FormRoom | null {
  if (rooms.has(id)) return rooms.get(id)!;
  let room: FormRoom | null = null;
  const d = derivedSonata(id);
  const secs = d?.piece.sections ?? [];
  const devIdx = secs.findIndex((s) => isLabel(s, /^development/));
  const recIdx = secs.findIndex((s) => isLabel(s, /^recapitulation/));
  if (d && devIdx > 0 && recIdx > devIdx) {
    const piece = d.piece;
    const view = buildView(piece);
    const home = parseKey(piece.homeKey);
    const expoSecs = secs.slice(0, devIdx);
    const recapSecs = secs.slice(recIdx);
    const expo = { start: expoSecs[0].start, end: expoSecs[expoSecs.length - 1].end, sections: expoSecs };
    const recap = { start: recapSecs[0].start, end: recapSecs[recapSecs.length - 1].end, sections: recapSecs };
    const barsIn = (a: number, b: number) => piece.measures.filter((m) => m.start >= a - 1e-6 && m.start < b - 1e-6);
    const eBars = barsIn(expo.start, expo.end);
    const rBars = barsIn(recap.start, recap.end);
    const ePrints = eBars.map((m) => printBar(piece.notes, m));
    const rPrints = rBars.map((m) => printBar(piece.notes, m));
    const pairs = alignBars(ePrints, rPrints);
    const columns: FormColumn[] = pairs.map((p) => {
      const em = p.i !== null ? eBars[p.i] : null;
      const rm = p.j !== null ? rBars[p.j] : null;
      const eKey = em ? keyAtBar(view, em.start) : null;
      const rKey = rm ? keyAtBar(view, rm.start) : null;
      let kind: ColumnKind;
      const broughtHome = !!(rKey && sameKey(rKey, home) && eKey && !sameKey(eKey, home));
      // Moving a major theme into a minor tonic changes its thirds and sixths; it is still the same music brought home.
      const modeChange = broughtHome && eKey!.mode !== rKey!.mode && p.sim >= MODE_FLOOR;
      if (!em) kind = 'new';
      else if (!rm) kind = 'cut';
      else if (modeChange) kind = 'home';
      else if (p.sim < SAME) kind = 'varied';
      else if (p.shift === 0) kind = 'same';
      else if (broughtHome) kind = 'home';
      else kind = 'moved';
      return { e: em?.bar ?? null, r: rm?.bar ?? null, sim: Math.round(p.sim * 100) / 100, shift: p.shift, kind, eKey, rKey, ...(modeChange ? { modeChange } : {}) };
    });
    const runs: FormRun[] = [];
    columns.forEach((c, i) => {
      if (c.kind !== 'varied' && c.kind !== 'cut' && c.kind !== 'new') return;
      const last = runs[runs.length - 1];
      if (last && last.to === i - 1) last.to = i;
      else runs.push({ kind: c.kind, from: i, to: i });
    });
    // A run takes the kind that dominates it.
    for (const r of runs) {
      const tally = new Map<string, number>();
      for (let i = r.from; i <= r.to; i++) tally.set(columns[i].kind, (tally.get(columns[i].kind) ?? 0) + 1);
      r.kind = [...tally.entries()].sort((a, b) => b[1] - a[1])[0][0] as FormRun['kind'];
    }
    const counts = { same: 0, home: 0, moved: 0, varied: 0, cut: 0, new: 0 } as Record<ColumnKind, number>;
    columns.forEach((c) => counts[c.kind]++);
    room = { id, piece, view, home, expo, recap, columns, runs, counts };
  }
  rooms.set(id, room);
  return room;
}

export const KIND_WORDS: Record<ColumnKind, { word: string; name: string }> = {
  same: { word: 'Returns as it was', name: 'Literal restatement' },
  home: { word: 'Returns, brought home', name: 'Transposed to the tonic' },
  moved: { word: 'Returns in another key', name: 'Transposed' },
  varied: { word: 'Rewritten', name: 'Altered in the recapitulation' },
  cut: { word: 'Only in the exposition', name: 'Omitted from the recapitulation' },
  new: { word: 'Only in the recapitulation', name: 'Added in the recapitulation' },
};

export function describeKeys(c: FormColumn): string {
  if (!c.eKey || !c.rKey) return '';
  return sameKey(c.eKey, c.rKey) ? keyName(c.eKey) : `${keyName(c.eKey)} → ${keyName(c.rKey)}`;
}

export const KIND_LETTER: Record<ColumnKind, string> = { same: '=', home: 'H', moved: 'M', varied: '~', cut: '-', new: '+' };
export const LETTER_KIND: Record<string, ColumnKind> = { '=': 'same', H: 'home', M: 'moved', '~': 'varied', '-': 'cut', '+': 'new' };
